/**
 * Centralized access-token lifecycle: refresh before expiry, single-flight refresh, session recovery.
 */

import { broadcastAuthLogout, broadcastAuthSessionExpired } from './authSync';
import { clearIamAccessProfile } from './iamAccess';
import { broadcastIamAuthLogout } from './iamProfileSync';
import { isDevIamLoginMode } from './authMode';
import { isCookieAuthMode } from './cookieAuthMode';
import { logoutKeycloakSession } from './keycloakClient';
import { getOidcConfiguration } from './oidcDiscovery';
import { captureCurrentWorkspaceRouteForReauth } from './pendingSmartLaunch';
import { decodeJwtPayload } from './jwtPayload';
import {
  getAccessTokenExpiresAtMs,
  getDevRefreshToken,
  getRefreshExpiresAtMs,
  getStoredAccessToken,
  hasDevAccessToken,
  isAccessTokenExpiringSoon,
  isRefreshTokenExpired,
  reloadTokenStateFromStorage,
  setDevAuthSession,
  clearTokenState,
} from './tokenStore';

export const SESSION_EXPIRED_MESSAGE =
  'Your session expired. Please sign in again to continue.';

export const STREAM_RECONNECTING_MESSAGE = 'Reconnecting securely...';

const DEFAULT_REFRESH_THRESHOLD_MS = 60_000;

function getRefreshThresholdMs(): number {
  const raw = (import.meta.env.VITE_TOKEN_REFRESH_THRESHOLD_MS ?? '').toString().trim();
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return DEFAULT_REFRESH_THRESHOLD_MS;
}

const sessionExpiredListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

export function handleAuthFailure(): void {
  captureCurrentWorkspaceRouteForReauth();
  clearTokenState();
  clearIamAccessProfile('auth-failure', { broadcast: false });
  broadcastIamAuthLogout();
  broadcastAuthSessionExpired();
  sessionExpiredListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener failures.
    }
  });
}

/**
 * #30 regression (2026-08-07): the token endpoint is the MarchAgent backend
 * itself, so a backend outage makes every refresh attempt fail for
 * INFRASTRUCTURE reasons while the refresh token is still perfectly valid.
 * Callers must be able to tell that apart from a real rejection: clearing
 * token state on an unreachable endpoint permanently kills the session even
 * after the backend comes back. `REFRESH_UNAVAILABLE` = "could not ask"
 * (network error, 5xx); `null` = "asked and was refused" (4xx, no refresh
 * token, refresh token expired).
 */
const REFRESH_UNAVAILABLE = Symbol('march-refresh-unavailable');
type RefreshOutcome = string | null | typeof REFRESH_UNAVAILABLE;

/**
 * SEC-I5 sentinel: the cookie session was refreshed successfully.
 *
 * Deliberately NOT a token string. In cookie mode there is no token for this
 * layer to hold, and returning a placeholder string would be attached as a
 * `Bearer` by any caller that treats a truthy result as a credential.
 */
const COOKIE_SESSION_REFRESHED = Symbol('march-cookie-session-refreshed');
type RefreshResult = RefreshOutcome | typeof COOKIE_SESSION_REFRESHED;

/**
 * Refresh in cookie mode (SEC-I5).
 *
 * The refresh token is httpOnly, so the browser cannot put it in the body —
 * the backend reads it from the cookie. `credentials: 'include'` is what makes
 * the cookie travel; without it this silently refreshes nothing.
 */
async function requestCookieRefreshGrant(): Promise<RefreshResult> {
  const config = await getOidcConfiguration();
  if (!config?.token_endpoint) return null;

  const body = new URLSearchParams({ grant_type: 'refresh_token' });

  try {
    const res = await fetch(config.token_endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!res.ok) return res.status >= 500 ? REFRESH_UNAVAILABLE : null;

    // The response carries no tokens by design, and nothing here needs its
    // body: the new cookies are already set by the time this resolves. Expiry
    // is deliberately NOT tracked client-side — in cookie mode the flow is
    // request -> 401 -> refresh -> retry, so a second copy of the expiry would
    // be state to keep correct for no behaviour it changes.
    return COOKIE_SESSION_REFRESHED;
  } catch {
    return REFRESH_UNAVAILABLE;
  }
}

/**
 * Refresh the httpOnly cookie session. True when the session is good again.
 *
 * Single-flighted with the header-mode refresh so a burst of 401s produces one
 * refresh, not one per request.
 */
export async function refreshCookieSession(): Promise<boolean> {
  const outcome = await refreshAccessTokenSingleFlight();
  return outcome === COOKIE_SESSION_REFRESHED;
}

async function requestRefreshTokenGrant(): Promise<RefreshResult> {
  if (isCookieAuthMode()) return requestCookieRefreshGrant();

  const refreshToken = getDevRefreshToken();
  if (!refreshToken) return null;
  if (isRefreshTokenExpired()) return null;

  const config = await getOidcConfiguration();
  if (!config?.token_endpoint) return null;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  try {
    const res = await fetch(config.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    // 5xx = the auth proxy itself is failing; nothing was decided about this
    // session. Anything else non-ok (4xx) is an explicit rejection.
    if (!res.ok) return res.status >= 500 ? REFRESH_UNAVAILABLE : null;

    const accessToken = String(payload.access_token ?? '').trim();
    // An ok response with no token is a malformed/degraded proxy reply, not
    // a rejection — keep the session and let a later attempt decide.
    if (!accessToken) return REFRESH_UNAVAILABLE;

    const newRefresh = String(payload.refresh_token ?? refreshToken).trim();
    const expiresIn = Number(payload.expires_in ?? 0);
    const refreshExpiresIn = Number(payload.refresh_expires_in ?? 0);
    const expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined;
    const refreshExpiresAt =
      refreshExpiresIn > 0 ? Date.now() + refreshExpiresIn * 1000 : (getRefreshExpiresAtMs() ?? undefined);

    setDevAuthSession({
      accessToken,
      refreshToken: newRefresh || undefined,
      expiresAt,
      refreshExpiresAt,
      idToken: String(payload.id_token ?? '').trim() || undefined,
      tokenType: String(payload.token_type ?? 'Bearer').trim(),
      claims: decodeJwtPayload(accessToken),
    });
    return accessToken;
  } catch {
    // fetch itself failed — endpoint unreachable (the observed outage shape).
    return REFRESH_UNAVAILABLE;
  }
}

let refreshOutcomePromise: Promise<RefreshResult> | null = null;

async function refreshAccessTokenSingleFlight(): Promise<RefreshResult> {
  if (refreshOutcomePromise) return refreshOutcomePromise;
  refreshOutcomePromise = requestRefreshTokenGrant().finally(() => {
    refreshOutcomePromise = null;
  });
  return refreshOutcomePromise;
}

/** Refresh access token via MarchAgent backend auth proxy (single-flight). */
export async function refreshAccessToken(): Promise<string | null> {
  if (!isDevIamLoginMode()) return null;
  const outcome = await refreshAccessTokenSingleFlight();
  return typeof outcome === 'string' ? outcome : null;
}

/** Return a valid access token, refreshing when near expiry (dev IAM modes). */
export async function getValidAccessToken(): Promise<string> {
  // SEC-I5: there is no readable token in cookie mode, and returning anything
  // truthy here would be attached as a Bearer by every caller.
  if (isCookieAuthMode()) return '';
  if (!isDevIamLoginMode()) {
    return '';
  }

  reloadTokenStateFromStorage();

  const stored = getStoredAccessToken();
  const threshold = getRefreshThresholdMs();

  if (stored && !isAccessTokenExpiringSoon(threshold)) {
    return stored;
  }

  const refreshed = await refreshAccessTokenSingleFlight();
  if (typeof refreshed === 'string' && refreshed) return refreshed;

  const expiresAt = getAccessTokenExpiresAtMs();
  if (stored && expiresAt && Date.now() < expiresAt) {
    return stored;
  }

  if (refreshed === REFRESH_UNAVAILABLE) {
    // #30: the refresh endpoint could not be reached — nothing proved this
    // session dead. Keep all token state so the next call retries and the
    // session self-recovers the moment the backend is back.
    return '';
  }

  handleAuthFailure();
  return '';
}

/** Force refresh (e.g. after 401). Returns null when session cannot be renewed. */
export async function forceRefreshAccessToken(): Promise<string | null> {
  if (!isDevIamLoginMode()) return null;
  // SEC-I5: cookie mode has no token to return. Callers wanting a renewed
  // cookie session use `refreshCookieSession()`; this one is the header path
  // and must never hand back the sentinel, which a caller would attach as a
  // Bearer. (The compiler caught exactly this.)
  if (isCookieAuthMode()) return null;
  const refreshed = await refreshAccessTokenSingleFlight();
  if (refreshed === REFRESH_UNAVAILABLE) {
    // #30: endpoint unreachable — this request fails, the session survives.
    return null;
  }
  if (typeof refreshed !== 'string' || !refreshed) {
    handleAuthFailure();
    return null;
  }
  return refreshed;
}

/** Bootstrap session on app load — silent refresh when access token expired but refresh is valid. */
export async function ensureAuthenticated(): Promise<boolean> {
  // SEC-I5: in cookie mode this layer cannot inspect the credential, so there
  // is nothing to check here. Authentication is proven by the first API call
  // succeeding: a 401 triggers refresh-then-retry, and a failed refresh runs
  // `handleAuthFailure()` exactly as it does for a dead bearer session.
  if (isCookieAuthMode()) return hasDevAccessToken();
  if (!isDevIamLoginMode()) return true;
  reloadTokenStateFromStorage();
  const token = await getValidAccessToken();
  return token.length > 0;
}

/** Revoke tokens through backend, clear local session, and broadcast logout. */
export async function logout(): Promise<void> {
  await logoutKeycloakSession();
  broadcastAuthLogout();
}
